/**
 * Bonus Issue Modal (Fondemission)
 *
 * Modal for registering bonus issue transactions.
 * A bonus issue creates new shares from retained earnings/reserves
 * and distributes them to existing shareholders proportionally.
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PlusCircle, Info, AlertCircle, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useRegisterBonusIssue,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings } from "@/lib/api";

interface BonusIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

const bonusIssueSchema = z.object({
  shareClassId: z.string().min(1, "Välj aktieslag"),
  ratio: z.coerce
    .number()
    .min(0.01, "Kvot måste vara minst 0.01")
    .max(100, "Kvot kan inte överstiga 100"),
  recordDate: z.string().min(1, "Avstämningsdag krävs"),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
});

type BonusIssueFormData = z.infer<typeof bonusIssueSchema>;

export function BonusIssueModal({
  isOpen,
  onClose,
  companyId,
}: BonusIssueModalProps) {
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerBonusIssue = useRegisterBonusIssue();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BonusIssueFormData>({
    resolver: zodResolver(bonusIssueSchema),
    defaultValues: {
      shareClassId: "",
      ratio: 1,
      recordDate: new Date().toISOString().split("T")[0],
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareClassId = watch("shareClassId");
  const watchRatio = watch("ratio");

  // Get selected share class
  const selectedShareClass = useMemo(() => {
    return shareClasses.find((sc: ShareClass) => sc.id === watchShareClassId);
  }, [shareClasses, watchShareClassId]);

  // Calculate preview of new shares per shareholder
  const shareholderPreview = useMemo(() => {
    if (!selectedShareClass || !watchRatio || watchRatio <= 0) return [];

    const activeShareholders = shareholders.filter(
      (s: ShareholderWithHoldings) => s.isActive
    );

    return activeShareholders
      .map((s: ShareholderWithHoldings) => {
        const holding = s.holdings.find(
          (h) => h.shareClass.id === watchShareClassId
        );
        const currentShares = holding?.totalShares || 0;
        const newShares = Math.floor(currentShares * watchRatio);
        const fractional = (currentShares * watchRatio) % 1;

        return {
          id: s.id,
          name: s.name,
          currentShares,
          newShares,
          fractional: fractional > 0 ? fractional.toFixed(4) : null,
        };
      })
      .filter((s) => s.currentShares > 0);
  }, [shareholders, selectedShareClass, watchShareClassId, watchRatio]);

  // Calculate total new shares
  const totalNewShares = useMemo(() => {
    return shareholderPreview.reduce((sum, s) => sum + s.newShares, 0);
  }, [shareholderPreview]);

  // Check for fractional shares warning
  const hasFractionalShares = useMemo(() => {
    return shareholderPreview.some((s) => s.fractional !== null);
  }, [shareholderPreview]);

  const onSubmit = async (data: BonusIssueFormData) => {
    try {
      await registerBonusIssue.mutateAsync({
        companyId,
        data: {
          shareClassId: data.shareClassId,
          ratio: data.ratio,
          recordDate: data.recordDate,
          transactionDate: data.transactionDate,
          notes: data.notes,
        },
      });
      toast.success(
        `Fondemission registrerad - ${totalNewShares.toLocaleString("sv-SE")} nya aktier`
      );
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera fondemission";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Fondemission"
      size="lg"
    >
      <div className="space-y-6">
        {/* Info box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <PlusCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-blue-900">Vad är fondemission?</p>
              <p className="text-sm text-blue-700 mt-1">
                Vid fondemission emitteras nya aktier genom överföring från fritt eget
                kapital (t.ex. balanserade vinstmedel) till aktiekapitalet. Aktierna
                fördelas proportionellt till befintliga aktieägare utan betalning.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag *
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")} aktier
                </option>
              ))}
            </select>
            {errors.shareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareClassId.message}
              </p>
            )}
          </div>

          {/* Ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calculator size={14} className="inline mr-1" />
              Emissionskvot (nya aktier per befintlig aktie) *
            </label>
            <input
              type="number"
              step="0.01"
              {...register("ratio")}
              className="input"
              min={0.01}
              max={100}
            />
            <p className="text-xs text-gray-500 mt-1">
              Exempel: 1 = 1 ny aktie per befintlig aktie (dubblar antalet), 0.5 = 1 ny
              aktie per 2 befintliga
            </p>
            {errors.ratio && (
              <p className="text-sm text-red-600 mt-1">{errors.ratio.message}</p>
            )}
          </div>

          {/* Record Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Avstämningsdag *
            </label>
            <input
              type="date"
              {...register("recordDate")}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Datum då aktieägarna ska vara registrerade för att delta i
              fondemissionen
            </p>
            {errors.recordDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.recordDate.message}
              </p>
            )}
          </div>

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Preview */}
          {selectedShareClass && shareholderPreview.length > 0 && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Info className="text-gray-600" size={16} />
                <p className="font-medium text-gray-900">
                  Förhandsvisning av fördelning
                </p>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {shareholderPreview.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-gray-700">{s.name}</span>
                    <span className="text-gray-900">
                      {s.currentShares.toLocaleString("sv-SE")} st →{" "}
                      <span className="font-medium text-green-700">
                        +{s.newShares.toLocaleString("sv-SE")} st
                      </span>
                      {s.fractional && (
                        <span className="text-amber-600 ml-1">
                          ({s.fractional} avrundad)
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between font-medium">
                <span>Totalt nya aktier:</span>
                <span className="text-green-700">
                  +{totalNewShares.toLocaleString("sv-SE")} st
                </span>
              </div>
            </div>
          )}

          {/* Fractional shares warning */}
          {hasFractionalShares && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle
                  className="text-amber-600 mt-0.5 flex-shrink-0"
                  size={16}
                />
                <p className="text-sm text-amber-800">
                  <strong>Observera:</strong> Med denna kvot uppstår bråkdelar av
                  aktier. Dessa avrundas nedåt. Överväg att justera kvoten för att
                  undvika detta.
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar om fondemissionen..."
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Krav för fondemission:</strong> Beslut fattas av bolagsstämman.
              Aktiekapitalökningen måste registreras hos Bolagsverket.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || shareholderPreview.length === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : (
                "Registrera fondemission"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
